"use client";

/* Drop-in video block for any SOP Step-7 video. One <VideoBlock slug="…"/>
 * renders: the player (poster-first, lazy-init — the poster is the LCP-safe
 * candidate, the player loads on click), the VideoObject schema, and the
 * on-page transcript (indexable + AEO-citable). Pulls everything from the
 * SITE_VIDEOS registry, so there's nothing to pass but the slug. */

import { useState } from "react";
import { getVideo, videoEmbedUrl, videoWatchUrl } from "@/lib/videos";
import { JsonLd, videoObjectJsonLd } from "@/lib/jsonld";

const SITE = process.env.NEXT_PUBLIC_SITE_URL || "https://digital.apexflowlabs.com";

function abs(u: string) {
  return u.startsWith("http") ? u : `${SITE}${u}`;
}

export function VideoBlock({ slug, className = "" }: { slug: string; className?: string }) {
  const v = getVideo(slug);
  const [playing, setPlaying] = useState(false);

  if (!v) return null;

  const poster = abs(v.thumbnailUrl);
  const muxMp4 = v.muxPlaybackId ? `https://stream.mux.com/${v.muxPlaybackId}/highest.mp4` : undefined;
  const directSrc = v.mp4 || muxMp4;

  return (
    <figure className={`overflow-hidden rounded-2xl border ${className}`} style={{ borderColor: "var(--color-border-default)", background: "#05070d" }}>
      <JsonLd
        data={videoObjectJsonLd({
          name: v.name,
          description: v.description,
          thumbnailUrl: poster,
          uploadDate: v.uploadDate,
          duration: v.duration,
          contentUrl: directSrc,
          embedUrl: videoEmbedUrl(v),
          transcript: v.transcript,
          chapters: v.chapters,
          watchUrl: videoWatchUrl(v),
          durationSeconds: v.durationSeconds,
        })}
      />

      <div className="relative aspect-video w-full bg-black">
        {!playing ? (
          // Poster-first facade — no player JS/iframe until the user clicks.
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full"
            aria-label={`Play: ${v.name}`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={poster}
              alt={v.name}
              className="h-full w-full object-cover"
              loading="lazy"
              /* hqdefault occasionally 404s even for public videos — fall back to
               * mqdefault (near-always present) so the poster never shows broken. */
              onError={(e) => {
                const img = e.currentTarget;
                if (v.youtubeId && !img.dataset.fallback) {
                  img.dataset.fallback = "1";
                  img.src = `https://i.ytimg.com/vi/${v.youtubeId}/mqdefault.jpg`;
                }
              }}
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span
                className="flex h-16 w-16 items-center justify-center rounded-full transition-transform group-hover:scale-110"
                style={{ background: "var(--color-accent)", color: "#0b0806" }}
              >
                ▶
              </span>
            </span>
          </button>
        ) : v.youtubeId ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${v.youtubeId}?autoplay=1`}
            title={v.name}
            allow="accelerated-2d-canvas; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        ) : directSrc ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            className="absolute inset-0 h-full w-full"
            poster={poster}
            controls
            autoPlay
            loop={v.loop}
            muted={v.loop}
            playsInline
          >
            {v.webm && <source src={v.webm} type="video/webm" />}
            {directSrc && <source src={directSrc} type="video/mp4" />}
            {v.captionsVtt && <track kind="captions" src={abs(v.captionsVtt)} srcLang="en" label="English" default />}
          </video>
        ) : null}
      </div>

      <figcaption className="p-5">
        <div className="text-[15px] font-medium text-white">{v.name}</div>
        <p className="mt-1 text-[13px] leading-[1.55] text-white/65">{v.description}</p>
        {v.transcript && (
          <details className="mt-3">
            <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.12em] text-white/65">
              Read the transcript
            </summary>
            <p className="mt-3 whitespace-pre-line text-[13px] leading-[1.65] text-white/65">{v.transcript}</p>
          </details>
        )}
      </figcaption>
    </figure>
  );
}
